import React, { useState, useMemo, useCallback } from "react";
import * as R from "ramda";
import { Modal, Tooltip } from "antd";
import { FieldArray, withFormik } from "formik";
import { Droppable, DragDropContext } from "react-beautiful-dnd";
import shortid from "shortid";
import { compose } from "recompose";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  SAVE_OPPORTUNITY_STEPS,
  UPDATE_OPPORTUNITY_STEPS,
  SAVE_OPPORTUNITY_QUESTIONS,
  GO_TO_PREVIOUS_WIZARD_STEP
} from "../../../../../store/actions/opportunity";
import { isLoading } from "../../../../../utils/actions";
import getChatbotMessage from "../../../../../utils/getChatbotMessage";
import CreateNewOpportunity from "../../../NewOpportunity/CreateNewOpportunity";
import Button from "../../../../../components/Button";
import { useProcessState } from "../hooks";

import ProcessStep from "./ProcessStep";

import styled from "styled-components/macro";

const mapIndexed = R.addIndex(R.map);

const parseSteps = R.pipe(
  R.defaultTo([]),
  R.reject(({ order }) => order === 99 || order === 98 || order === 0),
  R.sortBy(R.prop("order"))
);

const Wrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ isWizard }) => (isWizard ? "0" : "8px")};

  .process-editor_header {
    margin-bottom: 8px;
  }

  .process-editor_header__row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: space-between;
    align-items: center;
    padding: ${({ isWizard }) => (isWizard ? 0 : "24px 32px 0 32px")};

    .title {
      ${({ isWizard }) => !isWizard && `margin-bottom: 0;`};
    }
  }
  .steps-container {
    background-color: #f6f8fb;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 18px;
    padding: 12px 0;
    ${({ isWizard }) => isWizard && `margin: 0 -24px;`}
    ${({ hasItems }) => !hasItems && `padding: 0;`}
  }
  .first-step-btn {
    margin: 0 auto 15px;
    width: 220px;
  }
  .save-btn-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    margin: 0 24px 24px;
    p {
      margin: 0;
      font-weight: bold;
    }
  }
`;

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const defaultStep = (i, t) => ({
  name: `Paso ${i + 1}`,
  type: "presential-interview",
  config: {
    users: undefined,
    address: "",
    message: getChatbotMessage(
      { order: i + 1, type: "presential-interview" },
      t
    )
  },
  id: `temp-${shortid.generate()}`,
  unvalidated: true,
  unsaved: true
});

function ProcessEditor({
  values,
  handleSubmit,
  setValues,
  isWizard,
  goToPreviousStep,
  initialEditStepId,
  updateStep,
  initialSteps = [],
  savingSteps,
  onChangeEdit,
  setProcessHasChanged
}) {
  const { t } = useTranslation(["global", "opportunity"]);

  const selectedStep = useMemo(() => {
    return initialSteps.find(R.propEq("order", 98));
  }, [initialSteps]);

  const acceptedStep = useMemo(() => {
    return initialSteps.find(R.propEq("order", 99));
  }, [initialSteps]);

  const [editingStep, setEditingStep] = useState(
    [...values.steps, selectedStep || {}, acceptedStep || {}].some(
      ({ id }) => id === initialEditStepId
    ) // Only set editingStep to initial id value if step actually exists
      ? initialEditStepId
      : null
  );
  const createStep = ({ arrayHelpers }) => {
    const createNewStep = () => {
      const newStep = defaultStep(values.steps.length, t);
      arrayHelpers.push(newStep);
      setEditingStep(newStep.id);
    };

    if (editingStep) {
      Modal.confirm({
        title: t("opportunity:process.processEditor.editingSureDiscard"),
        onOk: createNewStep,
        okText: t("opportunity:process.processEditor.yesDiscard"),
        cancelText: t("opportunity:process.processEditor.noDiscard")
      });
    } else {
      createNewStep();
    }
  };

  const onDragEnd = result => {
    if (!result.destination) {
      return;
    }

    const newSteps = R.pipe(
      steps => reorder(steps, result.source.index, result.destination.index),
      mapIndexed((step, index) => R.assoc("order", index + 1, step))
    )(values.steps);

    setValues({ steps: newSteps });

    if (isWizard) handleSubmit();
  };

  const processHasChanged = useMemo(() => {
    const hasChanged = !R.equals(
      parseSteps(initialSteps).map(({ id, order }) => [id, order]),
      values.steps.map(({ id, order }) => [id, order])
    );
    setProcessHasChanged && setProcessHasChanged(hasChanged);
    return hasChanged;
  }, [initialSteps, values.steps, setProcessHasChanged]);

  const onSwitchEditMode = stepId => {
    setEditingStep(editingStep => {
      const newEditStepId = editingStep === stepId ? null : stepId;
      if (onChangeEdit && typeof onChangeEdit === "function") {
        onChangeEdit(newEditStepId);
      }
      return newEditStepId;
    });
  };

  const addStepIsDisabled = useMemo(() => {
    return (
      // Current step being edited is unvalidated OR max 30 step limit reached
      (editingStep &&
        R.pipe(
          R.find(R.propEq("id", editingStep)),
          R.propOr(false, "unvalidated")
        )(values.steps)) ||
      (values.steps && values.steps.length >= 30)
    );
  }, [editingStep, values.steps]);

  const onSave = (
    newStep,
    originalStep,
    resetEditingStep = true,
    arrayHelpers,
    i
  ) => {
    resetEditingStep && setEditingStep(null);
    arrayHelpers.replace(i, {
      ...originalStep,
      ...newStep,
      ...(originalStep.unvalidated ? { unvalidated: false } : {})
    });
    if (isWizard) handleSubmit();
  };

  const createOpportunityPopConfirm = useCallback(
    () =>
      setValues({
        steps: values.steps.filter(({ id }) => id !== editingStep)
      }),
    [editingStep, setValues, values.steps]
  );

  const createOpportunityConfirmButtonProps = useMemo(
    () => ({
      popProps: {
        title: t("opportunity:process.processEditor.editingSureDiscard"),
        disabled: !editingStep,
        onConfirm: createOpportunityPopConfirm,
        okText: t("opportunity:process.processEditor.yesDiscard"),
        cancelText: t("opportunity:process.processEditor.noDiscard")
      }
    }),
    [createOpportunityPopConfirm, editingStep, t]
  );

  const { matches } = useProcessState();

  return (
    <Wrapper
      as={editingStep ? "div" : "form"}
      onSubmit={handleSubmit}
      isWizard={isWizard}
      hasItems={values.steps.length > 0}
    >
      <DragDropContext onDragEnd={result => onDragEnd(result, values.steps)}>
        <FieldArray
          name="steps"
          render={arrayHelpers => (
            <>
              <div className="process-editor_header">
                <div className="process-editor_header__row">
                  <h1 className="title">
                    {isWizard
                      ? t("opportunity:process.processEditor.wizardTitle")
                      : t("opportunity:process.processEditor.title")}
                  </h1>
                  {!isWizard && (
                    <Button
                      type="tertiary"
                      icon="plus"
                      onClick={() => createStep({ arrayHelpers })}
                      disabled={addStepIsDisabled}
                      data-test="add-step-btn"
                      style={!isWizard ? { marginRight: 40 } : {}}
                    >
                      {t("opportunity:process.processEditor.addStep")}
                    </Button>
                  )}
                </div>
                {isWizard && <p>{t("opportunity:wizardProcess.subtitle")}</p>}
              </div>

              <Droppable droppableId="droppable">
                {provided => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="steps-container"
                  >
                    {values.steps.map((step, i) => (
                      <ProcessStep
                        allMatches={matches}
                        step={step}
                        index={i}
                        isEditing={editingStep}
                        editMode={editingStep === step.id}
                        switchEditMode={() => onSwitchEditMode(step.id)}
                        onSave={(newStep, resetEditingStep = true) =>
                          onSave(
                            newStep,
                            step,
                            resetEditingStep,
                            arrayHelpers,
                            i
                          )
                        }
                        isWizard={isWizard}
                        disabled={editingStep && editingStep !== step.id}
                        onRemove={
                          step.matches && step.matches.length
                            ? null
                            : () => {
                                setEditingStep(null);
                                arrayHelpers.remove(i);
                                if (isWizard) handleSubmit();
                              }
                        }
                        updateStep={updateStep}
                        key={step.id}
                      />
                    ))}
                    {provided.placeholder}
                    {/* Fixed Seleccionados step */}
                    {!isWizard && selectedStep && (
                      <ProcessStep
                        allMatches={matches}
                        isEditing={editingStep}
                        step={selectedStep}
                        editMode={editingStep === selectedStep.id}
                        switchEditMode={() => onSwitchEditMode(selectedStep.id)}
                        onSave={() => {
                          setEditingStep(null);
                        }}
                        isWizard={false}
                        disabled={
                          editingStep && editingStep !== selectedStep.id
                        }
                        updateStep={updateStep}
                        key={selectedStep.id}
                        draggable={false}
                      />
                    )}
                    {/* Fixed Oferta aceptada step */}
                    {!isWizard && acceptedStep && (
                      <ProcessStep
                        allMatches={matches}
                        isEditing={editingStep}
                        step={acceptedStep}
                        editMode={editingStep === acceptedStep.id}
                        switchEditMode={() => onSwitchEditMode(acceptedStep.id)}
                        onSave={() => {
                          setEditingStep(null);
                        }}
                        isWizard={false}
                        disabled={
                          editingStep && editingStep !== acceptedStep.id
                        }
                        updateStep={updateStep}
                        key={acceptedStep.id}
                        draggable={false}
                      />
                    )}
                  </div>
                )}
              </Droppable>
              {isWizard && (
                <Button
                  onClick={() => createStep({ arrayHelpers })}
                  disabled={addStepIsDisabled}
                  data-test="add-step-btn"
                  style={values.steps.length ? { marginTop: 25 } : {}}
                  className="first-step-btn"
                >
                  <span style={{ marginRight: "10px" }}>+</span>
                  {t("opportunity:process.processEditor.addStep")}
                </Button>
              )}
            </>
          )}
        />
      </DragDropContext>

      {isWizard && (
        <CreateNewOpportunity
          onGoToPrevious={goToPreviousStep}
          goToPreviousConfirmButtonProps={createOpportunityConfirmButtonProps}
          createOpportunityConfirmButtonProps={
            createOpportunityConfirmButtonProps
          }
          createAndPublishOpportunityConfirmButtonProps={
            createOpportunityConfirmButtonProps
          }
        />
      )}
      {!isWizard && (
        <div className="save-btn-row">
          {processHasChanged && (
            <p style={{ marginRight: 12 }}>
              {t("opportunity:process.processEditor.hasUnsavedChanges")}
            </p>
          )}
          <Tooltip
            title={
              editingStep &&
              t("opportunity:process.processEditor.saveStepBeforeProcess")
            }
          >
            <Button
              loading={savingSteps}
              disabled={!processHasChanged || editingStep}
              type="primary"
              size="lg"
              htmlType="submit"
              onClick={handleSubmit}
              style={{ alignSelf: "flex-end" }}
            >
              {t("opportunity:process.processEditor.saveProcess")}
            </Button>
          </Tooltip>
        </div>
      )}
    </Wrapper>
  );
}

export default compose(
  connect(
    state => ({
      opportunity: R.pathOr({}, ["opportunity", "selected"], state),
      savingSteps:
        isLoading(SAVE_OPPORTUNITY_STEPS, state) ||
        isLoading(UPDATE_OPPORTUNITY_STEPS, state) ||
        isLoading(SAVE_OPPORTUNITY_QUESTIONS, state)
    }),
    {
      goToPreviousStep: GO_TO_PREVIOUS_WIZARD_STEP.run
    }
  ),
  withFormik({
    mapPropsToValues: ({ initialSteps }) => ({
      steps: parseSteps(initialSteps)
    }),
    handleSubmit: ({ steps }, { props }) => {
      const { onSubmit } = props;
      onSubmit(steps);
    }
  })
)(ProcessEditor);
